import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import StatusUpdateModal from 'features/customer/components/StatusUpdateModal';
import KycManagementModal from 'features/customer/components/KycManagementModal';
import { useCustomer } from 'features/customer/hooks/useCustomer';
import { ArrowLeft, User, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AddressesTab, KycTab, OverviewTab, StatsTab, EmergencyContactsTab } from './components';
import { getStatusColor } from './utils';

const VALID_TABS = ['overview', 
  'addresses', 
  'emergency contacts'
  // 'stats'
];

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isPending, isError, error } = useCustomer(id);

  const tabFromUrl = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'overview';

  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isKycModalOpen, setIsKycModalOpen] = useState(false);

  const customer = data?.data?.customer;

  if (isPending) {
    return (
      <Layout>
        <div className='flex items-center justify-center min-h-screen'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto'></div>
            <p className='mt-4 text-muted-foreground'>Loading customer details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <div className='flex items-center justify-center min-h-screen'>
          <div className='text-center'>
            <XCircle className='w-12 h-12 text-destructive mx-auto mb-4' />
            <p className='text-destructive'>Error loading customer: {error?.message || 'Something went wrong'}</p>
            <button
              onClick={() => navigate(-1)}
              className='mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='min-h-screen bg-background text-foreground p-6'>
        <div className={PAGE_CONTENT_WIDTH}>
          {/* Header */}
          <div className='mb-6'>
            <button
              onClick={() => navigate(-1)}
              className='flex items-center space-x-2 text-muted-foreground hover:text-foreground mb-4 transition-colors'
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>

            <div className='flex items-start justify-between'>
              <div className='flex items-center space-x-4'>
                {customer?.profilePhoto ? (
                  <img
                    src={customer.profilePhoto}
                    alt={`${customer.firstName} ${customer.lastName}`}
                    className='w-20 h-20 rounded-full object-cover border-2 border-border'
                  />
                ) : (
                  <div className='w-20 h-20 rounded-full bg-muted flex items-center justify-center'>
                    <User size={32} className='text-muted-foreground' />
                  </div>
                )}
                <div>
                  <h1 className='text-2xl font-bold text-foreground'>
                    {customer?.firstName || customer?.lastName
                      ? `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim()
                      : 'Customer'}
                  </h1>
                  <p className='text-sm text-muted-foreground'>
                    {customer?.countryCode} {customer?.phoneNumber}
                  </p>
                  <div className='flex items-center gap-2 mt-2'>
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(
                        customer?.status
                      )}`}
                    >
                      {customer?.status?.replace('_', ' ')}
                    </span>
                    {/* <span
                      className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(
                        customer?.kyc?.status
                      )}`}
                    >
                      KYC: {customer?.kyc?.status?.replace('_', ' ')}
                    </span> */}
                  </div>
                </div>
              </div>

              <div className='flex gap-2'>
                <button
                  onClick={() => setIsStatusModalOpen(true)}
                  className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
                >
                  Update Status
                </button>
                {/* <button
                  onClick={() => setIsKycModalOpen(true)}
                  className='px-4 py-2 text-sm border border-border rounded-md bg-card hover:bg-accent transition-colors'
                >
                  Manage KYC
                </button> */}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className='mb-6'>
            <div className='border-b border-border'>
              <nav className='-mb-px flex space-x-8'>
                {VALID_TABS.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors capitalize ${
                      activeTab === tab
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className='space-y-6'>
            {activeTab === 'overview' && <OverviewTab customer={customer} />}
            {activeTab === 'kyc' && <KycTab customer={customer} customerId={id} />}
            {activeTab === 'addresses' && <AddressesTab customer={customer} customerId={id} />}
            {activeTab === 'emergency contacts' && <EmergencyContactsTab customer={customer} customerId={id} />}
            {activeTab === 'stats' && <StatsTab customer={customer} />}
          </div>
        </div>
      </div>

      {/* Modals */}
      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        customer={customer}
      />
      <KycManagementModal isOpen={isKycModalOpen} onClose={() => setIsKycModalOpen(false)} customer={customer} />
    </Layout>
  );
};

export default CustomerDetails;
